import { createFeature, createReducer, on } from '@ngrx/store';

import { ScannedCode } from 'app/features/scan-flow/models';
import {
    getLocationNumbersFromScannedCodes,
    isActivityMatchLocationType,
    isActivitySupportedForScanFlow,
    isBatchRequired,
    isSingleAmountResultRequired,
} from 'app/features/scan-flow/utils';
import { ProductBoxRegistration } from 'app/shared/components/product-box/models';
import { isRequiredActivityEntityEqual } from 'app/shared/features/activity-selector/utils';
import { RowSelectionItem } from 'app/shared/features/map-row-selector/models';
import {
    filterRowsSelectionByScannedCodes,
    sortRowSelection,
} from 'app/shared/features/map-row-selector/utils';
import { done, getInitialProgressState, ProgressState, running } from 'app/shared/models';
import { resetProductBoxRegistrationAmounts, sortProductBoxRegistrations } from 'app/shared/utils';
import { resetApiConfig } from 'app/state/api-config/api-config.actions';

import {
    addScanFlowCode,
    applyScanFlowRegistrationDataFromStorage,
    cancelScheduledScanFlowRegistration,
    changeScanFlowRowSelection,
    initScanFlow,
    initScanFlowSuccess,
    removeScannedCodeFromFlow,
    resetPartialScanFlowRegistrationData,
    resetScanFlowRegistrationData,
    scheduleScanFlowRegistration,
    setActivityInScanFlow,
    setBatchInScanFlow,
    setScanFlowAmountResult,
    setScanFlowEmployeeNumber,
    setScanFlowProductBoxRegistrations,
    userInputForScanFlowEnded,
    userInputForScanFlowStarted,
} from './scan-flow.actions';
import {
    getActivityCode,
    getProductBoxRegistrationsForNewActivity,
    sortScannedCodes,
    takeValidScannedCodesForNewActivity,
} from './scan-flow.utils';

export const scanFlowFeatureKey = 'scanFlow';

export interface ScanFlowRegistrationData {
    amountResult: number | undefined;
    batchNumber: number | undefined;
    employeeNumber: number | undefined;
    productBoxRegistrations: ProductBoxRegistration[];
    rowSelection: RowSelectionItem[];
    scannedCodes: ScannedCode[];
}

export interface ScanFlowState {
    data: ScanFlowRegistrationData;
    initializationProgress: ProgressState;
    scheduledRegistrationDate: number | undefined;
    waitingForUserInput: boolean;
}

export const emptyScanFlowRegistrationData: ScanFlowRegistrationData = {
    amountResult: undefined,
    batchNumber: undefined,
    employeeNumber: undefined,
    productBoxRegistrations: [],
    rowSelection: [],
    scannedCodes: [],
};

export const initialState: ScanFlowState = {
    data: emptyScanFlowRegistrationData,
    initializationProgress: getInitialProgressState(),
    scheduledRegistrationDate: undefined,
    waitingForUserInput: false,
};

export const reducer = createReducer(
    initialState,
    on(initScanFlow, (state) => {
        if (state.initializationProgress.done) return state;
        return { ...state, initializationProgress: running() };
    }),
    on(initScanFlowSuccess, (state) => ({ ...state, initializationProgress: done() })),
    on(resetScanFlowRegistrationData, resetApiConfig, (state) => ({
        ...state,
        data: emptyScanFlowRegistrationData,
    })),
    on(resetPartialScanFlowRegistrationData, (state) => ({
        ...state,
        data: {
            ...emptyScanFlowRegistrationData,
            employeeNumber: state.data.employeeNumber,
        },
    })),
    on(applyScanFlowRegistrationDataFromStorage, (state, { registrationDataFromStorage }) => ({
        ...state,
        initializationProgress: done(),
        data: registrationDataFromStorage ?? emptyScanFlowRegistrationData,
    })),
    on(addScanFlowCode, (state, { scannedCode }) => {
        const oldCodes = state.data.scannedCodes;
        const oldCodesWithoutBatch = oldCodes.filter((code) => code.codeType !== 'batch');
        const isScannedCodeNumberExist = oldCodes.some((oldCode) => oldCode.code === scannedCode.code);

        let newCodes: ScannedCode[] = [];
        let rowSelection = state.data.rowSelection;
        let batchNumber: number | undefined;

        switch (scannedCode.codeType) {
            case 'batch':
                if (isScannedCodeNumberExist) return state;
                // If new batch code is scanned, replace the existing batch code.
                newCodes = sortScannedCodes([...oldCodesWithoutBatch, scannedCode]);
                batchNumber = scannedCode.number;
                break;
            default:
                if (isScannedCodeNumberExist) return state;
                if (scannedCode.codeType === 'location' && scannedCode.number)
                    rowSelection = sortRowSelection([
                        ...rowSelection,
                        { rowNumber: scannedCode.number, registrationStatus: 'done' },
                    ]);

                newCodes = sortScannedCodes([...oldCodes, scannedCode]);
                break;
        }

        return {
            ...state,
            data: {
                ...state.data,
                batchNumber,
                scannedCodes: newCodes.filter((newCode) => newCode !== null),
                rowSelection,
            },
        };
    }),
    on(setScanFlowEmployeeNumber, (state, { employeeNumber }) =>
        state.data.employeeNumber === employeeNumber
            ? state
            : {
                  ...state,
                  data: { ...state.data, employeeNumber },
              },
    ),
    on(setScanFlowAmountResult, (state, { amountResult }) =>
        state.data.amountResult === amountResult
            ? state
            : {
                  ...state,
                  data: { ...state.data, amountResult },
              },
    ),
    on(setScanFlowProductBoxRegistrations, (state, { updatedProductBoxRegistrations }) => {
        const sortedCurrentRegistrations = sortProductBoxRegistrations(state.data.productBoxRegistrations);
        const sortedUpdatedRegistrationList = sortProductBoxRegistrations(updatedProductBoxRegistrations);

        const currentListJson = JSON.stringify(sortedCurrentRegistrations);
        const updatedListJson = JSON.stringify(sortedUpdatedRegistrationList);

        const registrationsRemainTheSame = currentListJson === updatedListJson;
        if (registrationsRemainTheSame) {
            return state;
        }

        return {
            ...state,
            data: {
                ...state.data,
                productBoxRegistrations: sortedUpdatedRegistrationList,
            },
        };
    }),
    on(setActivityInScanFlow, (state, action) => {
        const newActivityCode = action.activityCode;
        const newBatchCode = action.batchCode;
        if (!newActivityCode) {
            return {
                ...state,
                data: {
                    ...state.data,
                    amountResult: undefined,
                    productBoxRegistrations: [],
                    rowSelection: [],
                    scannedCodes: [],
                },
            };
        }

        const newActivity = newActivityCode.entity;
        if (!isActivitySupportedForScanFlow(newActivity)) {
            return state;
        }

        const currentActivityCode = getActivityCode(state.data.scannedCodes);

        // After submit a registration, the activity number remains the same
        if (currentActivityCode?.number === newActivityCode.number) {
            if (
                action.keepRegistrationData &&
                isRequiredActivityEntityEqual(newActivityCode.entity, currentActivityCode?.entity)
            ) {
                return state;
            }

            const prefilledProductBoxRegistrations = resetProductBoxRegistrationAmounts(
                state.data.productBoxRegistrations,
            );
            const locationScannedCodes =
                state.data.scannedCodes.find((code) => code.codeType === 'location') || null;
            const batchScannedCodes =
                state.data.scannedCodes.find((code) => code.codeType === 'batch') || null;

            let updatedScannedCodes = [];
            if (action.isContainerMeasurement === true) {
                updatedScannedCodes = [newActivityCode, locationScannedCodes, batchScannedCodes].filter(
                    (newScannedCode) => newScannedCode !== null,
                );

                return {
                    ...state,
                    data: {
                        ...state.data,
                        amountResult: undefined,
                        productBoxRegistrations: prefilledProductBoxRegistrations,
                        rowSelection: state.data.rowSelection,
                        scannedCodes: updatedScannedCodes,
                    },
                };
            }

            updatedScannedCodes = [newActivityCode, batchScannedCodes].filter(
                (newScannedCode) => newScannedCode !== null,
            );
            return {
                ...state,
                data: {
                    ...state.data,
                    amountResult: undefined,
                    productBoxRegistrations: prefilledProductBoxRegistrations,
                    rowSelection: [],
                    scannedCodes: updatedScannedCodes,
                },
            };
        }

        const isLocationTypeSimilar = isActivityMatchLocationType(
            newActivity,
            currentActivityCode?.entity?.locationType ?? null,
        );

        const newScannedCodes: ScannedCode[] = action.keepRegistrationData
            ? takeValidScannedCodesForNewActivity(
                  state.data.scannedCodes,
                  newActivityCode,
                  isLocationTypeSimilar,
              )
            : [newActivityCode, ...(newBatchCode ? [newBatchCode] : [])];

        const newRowSelection = filterRowsSelectionByScannedCodes({
            rowSelection: state.data.rowSelection,
            scannedCodes: newScannedCodes,
        });
        const newAmountResult =
            action.keepRegistrationData && isSingleAmountResultRequired(newActivity)
                ? state.data.amountResult
                : undefined;

        const newProductBoxRegistrations = getProductBoxRegistrationsForNewActivity({
            newActivity,
            keepRegistrationData: action.keepRegistrationData,
            currentProductBoxRegistrations: state.data.productBoxRegistrations,
        });

        const newBatchNumber =
            newBatchCode?.number ??
            (action.keepRegistrationData && isBatchRequired(newActivity)
                ? state.data.batchNumber
                : undefined);

        return {
            ...state,
            data: {
                ...state.data,
                batchNumber: newBatchNumber,
                amountResult: newAmountResult,
                productBoxRegistrations: newProductBoxRegistrations,
                rowSelection: sortRowSelection(newRowSelection),
                scannedCodes: newScannedCodes.filter((newScannedCode) => newScannedCode !== null),
            },
        };
    }),
    on(setBatchInScanFlow, (state, { batchCode }) => {
        // If there's an existing batch code, we remove it from the scanned codes and add the new one.
        const scannedCodes = state.data.scannedCodes.filter((code) => code.codeType !== 'batch');
        return {
            ...state,
            data: {
                ...state.data,
                batchNumber: batchCode.number,
                scannedCodes: [...scannedCodes, batchCode],
            },
        };
    }),
    on(changeScanFlowRowSelection, (state, { rowNumber, registrationStatus }) => {
        const rowNumbers = getLocationNumbersFromScannedCodes(state.data.scannedCodes);
        if (!rowNumbers.includes(rowNumber)) return state;

        const oldRowSelection = state.data.rowSelection;

        if (
            oldRowSelection.find(
                (currentRowSelectionItem) =>
                    currentRowSelectionItem.rowNumber === rowNumber &&
                    currentRowSelectionItem.registrationStatus === registrationStatus,
            )
        ) {
            return state;
        }

        const newRowSelection = sortRowSelection([
            { rowNumber, registrationStatus },
            ...oldRowSelection.filter(
                (currentRowSelectionItem) => currentRowSelectionItem.rowNumber !== rowNumber,
            ),
        ]);

        return { ...state, data: { ...state.data, rowSelection: newRowSelection } };
    }),

    on(removeScannedCodeFromFlow, (state, { code }) => {
        const filteredScannedCodes = state.data.scannedCodes.filter(
            (existingCode) => existingCode.code !== code,
        );
        if (state.data.scannedCodes.length === filteredScannedCodes.length) return state;

        const removedCode = state.data.scannedCodes.find((existingCode) => existingCode.code === code);
        let rowSelection = state.data.rowSelection;
        if (removedCode?.codeType === 'location') {
            rowSelection = rowSelection.filter((r) => r.rowNumber !== removedCode.number);
        }

        return {
            ...state,
            data: {
                ...state.data,
                scannedCodes: filteredScannedCodes,
                rowSelection,
            },
        };
    }),
    on(userInputForScanFlowStarted, (state) => ({
        ...state,
        waitingForUserInput: true,
    })),
    on(userInputForScanFlowEnded, (state) => ({
        ...state,
        waitingForUserInput: false,
    })),
    on(scheduleScanFlowRegistration, (state, { scheduledRegistrationDate }) => ({
        ...state,
        scheduledRegistrationDate,
    })),
    on(
        cancelScheduledScanFlowRegistration,
        (state): ScanFlowState => ({
            ...state,
            scheduledRegistrationDate: undefined,
        }),
    ),
);

export const scanFlowFeature = createFeature({
    name: scanFlowFeatureKey,
    reducer,
});
